/** @format */

import { useEffect, useState, useContext } from "react";
import styled from "styled-components";
import { fonts, mixins, colors } from "../../css/variables";
import { ErrorHandler } from "../../api/ErrorHandler";
import { Container } from "../../components/layouts/Container";
import { OcnkEntrySelectElement } from "../../components/ui_elements";
import { Box } from "../../components/layouts/Box";
import { Button } from "../../components/ui-parts";
import { Context } from "../../stores/Provider";
import useEntrySelectHooks from "../../hooks/useEntrySelectHooks";
import useNewEntryRegisterHooks from "../../hooks/useNewEntryRegisterHooks";
import useNavigateByAgent from "../../hooks/useNavigateByAgentHooks";

const Wrapper = styled.div`
  max-width: calc(700px);
  margin: 0px auto;
`;
const TopWrapper = styled.div`
  padding-bottom: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid ${colors.themeColor("border_box")};
`;

const TopTitle = styled.h1`
  margin: 0 0 8px;
  line-height: 100%;
  font-size: ${fonts.fontSizeRem("h1_desktop")};
  ${mixins.screenUnder()} {
    font-size: ${fonts.fontSizeRem("h1_mobile")};
  }
`;

const TopTitleExplain = styled.p`
  margin-bottom: 20px;
  font-size: ${fonts.fontSizeRem("m")};
  ${mixins.screenUnder()} {
    font-size: ${fonts.fontSizeRem("xs")};
  }
`;

const Title = styled.h1`
  font-size: ${fonts.fontSizeRem("xxl")};
  ${mixins.screenUnder()} {
    font-size: ${fonts.fontSizeRem("xl")};
  }
`;

const EntryWrapper = styled.div<{ showPlanLabel?: boolean }>`
  padding-top: ${(props) => (props.showPlanLabel ? "28px" : "0")};
  padding-bottom: 20px;
`;

type EntryInfo = {
  entry_id: number;
  register_at: string | null;
  status: string;
  shop_name: string | null;
  shop_website_url: string | null;
  account_id: string | null;
  plan_name: string;
  close_date: string | null;
};

const EntrySelect = () => {
  const { state } = useContext(Context);
  const { navigateByAgent } = useNavigateByAgent();
  const { ErrorHandle } = ErrorHandler(navigateByAgent);
  const { GetEntryInfo, entryInfoData, entrySelectNormal, entrySelectAbnormal, zAccountName } = useEntrySelectHooks();
  const { NewEntryInfo, newEntryRegisterNormal, newEntryRegisterAbnormal } = useNewEntryRegisterHooks();
  const [isShowFlag, setIsShowFlag] = useState<boolean>(false);

  const entrySelectHandler = async (entry_id: number) => {
    const response = await GetEntryInfo(entry_id);
    ErrorHandle(response, entrySelectNormal, entrySelectAbnormal, true);
  };

  const newEntryRegisterHandler = async () => {
    const response = await NewEntryInfo();
    ErrorHandle(response, newEntryRegisterNormal, newEntryRegisterAbnormal, true);
  };

  useEffect(() => {
    // 1件のみかつ「申込中」はダッシュボードに遷移する
    const handleEntry = async () => {
      if (
        entryInfoData &&
        Object.keys(entryInfoData).length === 1 &&
        entryInfoData["0"]["status"] === "ENTRING" &&
        entryInfoData["0"]?.entry_id
      ) {
        const entry_id = entryInfoData["0"].entry_id;
        entrySelectHandler(entry_id);
      }
      setIsShowFlag(true);
    };
    handleEntry();
  }, [entryInfoData]);

  if (state.isLoading) {
    return <Container />;
  }

  return (
    <Container isLogin showClosingNoticeText={false}>
      {isShowFlag && entryInfoData && (
        <Wrapper>
          <TopWrapper>
            <TopTitle>新規お申し込み</TopTitle>
            <TopTitleExplain>
              新しいショップでゼウス決済サービスをお申し込みされる方は、「新規のお申し込みに進む」ボタンを選択してください。
            </TopTitleExplain>
            <Button onClick={() => newEntryRegisterHandler()} size="large" sizeDesktop="large">
              新規のお申し込みに進む
            </Button>
          </TopWrapper>
          <TopTitle>お申し込み履歴</TopTitle>
          <TopTitleExplain>
            既にお申し込み中の登録を再開される方、またはお申し込み内容や進捗を確認されたい方は、対象の申込IDの「ダッシュボードへ進む」ボタンをクリックして、お進みください。
          </TopTitleExplain>
          <Title>{zAccountName}</Title>
          {Object.entries(entryInfoData).length === 0 ? (
            <div>有効なお申し込み履歴がありません。</div>
          ) : (
            Object.entries(entryInfoData).map(([key, value]) => {
              const entry = value as EntryInfo;
              return (
                <EntryWrapper key={key} showPlanLabel={false}>
                  {key === "0" ? <></> : <Box />}
                  <OcnkEntrySelectElement entryData={entry} entrySelectHandler={entrySelectHandler} />
                </EntryWrapper>
              );
            })
          )}
        </Wrapper>
      )}
    </Container>
  );
};

export default EntrySelect;
